import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Decimal } from 'decimal.js'
import { userApi, configApi } from '@/services/api'
import { useAuth } from './AuthContext'
import { useSSE } from './SSEContext'
import type { UserBalance } from '@/types'
import type { AmountChangeEvent } from '@/types/sse'

interface BalanceUpdate {
    event: string
    code: string
    amount: number
    action: 'Up' | 'Down'
}

interface BalanceContextType {
    balances: Record<string, UserBalance>
    mainCurrencyBalance: UserBalance | null
    mainCurrency: string | null
    isLoading: boolean
    updateBalance: (update: BalanceUpdate) => void
    refreshBalance: (currencyCode?: string) => Promise<void>
    getBalanceForCurrency: (currencyCode: string) => UserBalance | null
    // Decimal utility functions
    formatBalance: (amount: string | number, decimals?: number) => string
    addAmounts: (amount1: string | number, amount2: string | number) => number
    subtractAmounts: (amount1: string | number, amount2: string | number) => number
    compareAmounts: (amount1: string | number, amount2: string | number) => number
}

const BalanceContext = createContext<BalanceContextType | undefined>(undefined)

interface BalanceProviderProps {
    children: ReactNode
}

export function BalanceProvider({ children }: BalanceProviderProps) {
    const { isAuthenticated } = useAuth()
    const { addEventListener } = useSSE()
    const queryClient = useQueryClient()
    const [balances, setBalances] = useState<Record<string, UserBalance>>({})

    // Get main currency from config
    const { data: config } = useQuery({
        queryKey: ['config'],
        queryFn: () => configApi.getConfig(),
        enabled: isAuthenticated,
        retry: false,
    })

    const mainCurrency = config?.data?.data?.mainCurrency || null

    // Get all balances
    const { data: balanceList, isLoading: balanceListLoading } = useQuery({
        queryKey: ['user', 'balance', 'list'],
        queryFn: async () => {
            const response = await userApi.getUserBalanceList()
            return response.data.data.balanceList
        },
        enabled: isAuthenticated,
        retry: false,
        staleTime: 30 * 1000, // 30 seconds
    })

    // Get main currency balance (for backwards compatibility and priority loading)
    const { data: mainCurrencyBalanceData, isLoading: mainCurrencyLoading } = useQuery({
        queryKey: ['user', 'balance', mainCurrency],
        queryFn: () => userApi.getUserCurrencyBalance(mainCurrency!),
        enabled: isAuthenticated && !!mainCurrency,
        retry: false,
        staleTime: 30 * 1000, // 30 seconds
    })

    const isLoading = balanceListLoading || mainCurrencyLoading

    // Update local balances state when query data changes
    useEffect(() => {
        if (balanceList) {
            const balanceMap: Record<string, UserBalance> = {}
            balanceList.forEach(balance => {
                balanceMap[balance.currency.code] = balance
            })
            setBalances(balanceMap)
        }
    }, [balanceList])

    // Also update main currency balance if available (for immediate updates)
    useEffect(() => {
        if (mainCurrencyBalanceData?.data?.data?.balance && mainCurrency) {
            setBalances(prev => ({
                ...prev,
                [mainCurrency]: mainCurrencyBalanceData.data.data.balance
            }))
        }
    }, [mainCurrencyBalanceData, mainCurrency])

    const updateBalance = React.useCallback((update: BalanceUpdate) => {
        const { code, amount, action } = update

        setBalances(prev => {
            const currentBalance = prev[code]
            if (!currentBalance) {
                // If we don't have this currency balance, we can't update it
                // We should fetch it from the server instead
                console.warn(`No existing balance found for currency ${code}, fetching from server`)
                refreshBalance(code)
                return prev
            }

            try {
                // Parse the new amount using Decimal for precise calculations
                const newAmount = new Decimal(amount ?? 0)

                const newBalance = action === "Up" ? Decimal(currentBalance.balance).plus(newAmount) : Decimal(currentBalance.balance).minus(newAmount)

                const updatedBalance: UserBalance = {
                    ...currentBalance,
                    balance: newBalance.toNumber()
                }

                // Also update React Query cache
                queryClient.setQueryData(['user', 'balance', code], {
                    data: { data: { balance: updatedBalance } }
                })

                // Update the balance list cache as well
                const currentBalanceList = queryClient.getQueryData(['user', 'balance', 'list']) as UserBalance[]
                if (currentBalanceList) {
                    const updatedBalanceList = currentBalanceList.map(balance =>
                        balance.currency.code === code ? updatedBalance : balance
                    )
                    queryClient.setQueryData(['user', 'balance', 'list'], updatedBalanceList)
                }

                return {
                    ...prev,
                    [code]: updatedBalance
                }
            } catch (error) {
                console.error('Error updating balance with decimal calculation:', error, { code, amount, action })
                return prev
            }
        })
    }, [queryClient])

    // Listen for amountChange SSE events and automatically update balance
    useEffect(() => {
        const cleanup = addEventListener('amountChange', (data: any) => {
            if (data.event === 'amountChange') {
                console.log('Balance update received via SSE:', data)
                updateBalance(data as AmountChangeEvent)
            }
        })

        return cleanup
    }, [addEventListener, updateBalance])

    const refreshBalance = React.useCallback(async (currencyCode?: string) => {
        if (currencyCode) {
            // Refresh specific currency balance
            await queryClient.invalidateQueries({ queryKey: ['user', 'balance', currencyCode] })
        } else {
            // Refresh all balances
            await queryClient.invalidateQueries({ queryKey: ['user', 'balance'] })
        }
    }, [queryClient])

    const getBalanceForCurrency = React.useCallback((currencyCode: string): UserBalance | null => {
        return balances[currencyCode] || null
    }, [balances])

    // Decimal utility functions
    const formatBalance = React.useCallback((amount: string | number, decimals: number = 8): string => {
        try {
            const decimal = new Decimal(amount)
            return decimal.toFixed(decimals)
        } catch (error) {
            console.error('Error formatting balance:', error)
            return '0'
        }
    }, [])

    const addAmounts = React.useCallback((amount1: string | number, amount2: string | number): number => {
        try {
            const decimal1 = new Decimal(amount1)
            const decimal2 = new Decimal(amount2)
            return decimal1.plus(decimal2).toNumber()
        } catch (error) {
            console.error('Error adding amounts:', error)
            return 0
        }
    }, [])

    const subtractAmounts = React.useCallback((amount1: string | number, amount2: string | number): number => {
        try {
            const decimal1 = new Decimal(amount1)
            const decimal2 = new Decimal(amount2)
            return decimal1.minus(decimal2).toNumber()
        } catch (error) {
            console.error('Error subtracting amounts:', error)
            return 0
        }
    }, [])

    const compareAmounts = React.useCallback((amount1: string | number, amount2: string | number): number => {
        try {
            const decimal1 = new Decimal(amount1)
            const decimal2 = new Decimal(amount2)
            return decimal1.comparedTo(decimal2)
        } catch (error) {
            console.error('Error comparing amounts:', error)
            return 0
        }
    }, [])

    const mainCurrencyBalance = mainCurrency ? balances[mainCurrency] || null : null

    const value: BalanceContextType = {
        balances,
        mainCurrencyBalance,
        mainCurrency,
        isLoading,
        updateBalance,
        refreshBalance,
        getBalanceForCurrency,
        formatBalance,
        addAmounts,
        subtractAmounts,
        compareAmounts,
    }

    return (
        <BalanceContext.Provider value={value}>
            {children}
        </BalanceContext.Provider>
    )
}

export function useBalance() {
    const context = useContext(BalanceContext)
    if (context === undefined) {
        throw new Error('useBalance must be used within a BalanceProvider')
    }
    return context
}